import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircle, X, PaperPlaneRight, Sparkle } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";

function sessionId() {
  let id = localStorage.getItem("ppc_chat_session");
  if (!id) {
    id = "chat_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("ppc_chat_session", id);
  }
  return id;
}

export const ChatWidget = ({ open, setOpen }) => {
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(sessionId());

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t("chat_welcome") }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);

    const append = (delta) =>
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        copy[copy.length - 1] = { ...last, content: last.content + delta };
        return copy;
      });
    const replaceLast = (content) =>
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content };
        return copy;
      });

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let gotError = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) append(data.delta);
            else if (data.error) gotError = true;
          } catch {
            /* ignore partial */
          }
        }
      }
      if (gotError) replaceLast(t("chat_error"));
    } catch {
      replaceLast(t("chat_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        className="btn-primary fixed bottom-6 right-6 z-[60] hidden h-14 w-14 place-items-center rounded-2xl md:grid"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        aria-label="Chat"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <ChatCircle size={24} weight="fill" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-window"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="card-focal fixed bottom-24 right-4 z-[60] flex h-[520px] w-[92vw] max-w-sm flex-col overflow-hidden md:right-6"
            style={{ background: "linear-gradient(165deg, rgba(145,132,217,0.16), rgba(22,24,38,0.98) 60%)", backdropFilter: "blur(18px)" }}
          >
            <div className="flex items-center gap-3 border-b border-ppc px-5 py-4">
              <span className="avatar-grad grid h-9 w-9 place-items-center rounded-xl">
                <Sparkle size={18} weight="fill" />
              </span>
              <div>
                <p className="text-sm text-ppc-text">{t("chat_title")}</p>
                <p className="text-xs text-ppc-muted">{t("chat_sub")}</p>
              </div>
              <button type="button" aria-label="Close" data-testid="chat-close-btn" onClick={() => setOpen(false)} className="ml-auto text-ppc-muted md:hidden"><X size={20} /></button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-msg-${m.role}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] whitespace-pre-line rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "st-accepted"
                        : "border border-ppc bg-ppc-bg text-ppc-text"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && messages[messages.length - 1]?.content === "" && (
                <div className="flex justify-start">
                  <div className="flex gap-1 rounded-2xl border border-ppc bg-ppc-bg px-4 py-3">
                    {[0, 1, 2].map((i) => (
                      <motion.span
                        key={i}
                        className="h-2 w-2 rounded-full bg-ppc-muted"
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-ppc p-3">
              <div className="search-inset flex items-center gap-2 rounded-2xl px-2 py-1">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("chat_placeholder")}
                  className="flex-1 bg-transparent px-3 py-2 text-sm text-ppc-text outline-none placeholder:text-ppc-muted"
                />
                <Button
                  size="icon"
                  data-testid="chat-send-btn"
                  onClick={send}
                  disabled={busy}
                  className="btn-primary h-9 w-9 shrink-0 rounded-xl"
                >
                  <PaperPlaneRight size={16} />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
